<template>
  <header class="bg-white site-header">
    <MaintenanceBanner v-if="maintenanceFlag" />
    <div v-if="environmentBanner" class="environment-banner-clip">
      <div class="environment-banner">{{ $t("header.environmentBanner") }}</div>
    </div>
    <nav class="flex items-center justify-between lfd-nav">
      <div class="flex lg:flex-1 header-left">
        <a :href="DOMPurify.sanitize(maerskHomeURL)" class="-m-1.5 p-1.5 header-brand" rel="noopener noreferrer">
          <img class="h-8 w-auto header-logo" :src="maerskLogo" alt="Maersk Logo" />
        </a>
        <span class="header-line"></span>
        <a :href="DOMPurify.sanitize(redirectUri)" class="-m-1.5 p-1.5 app-title" rel="noopener noreferrer">
          <img class="lfd-logo" :src="lfdLogo" alt="LFD Logo" />
          <span class="product-name">{{ $t(`header.productName`) }}</span>
        </a>
        <div v-if="isAdmin" class="page-view-dropdown">
          <mc-select
            name="select"
            fit="small"
            hiddenlabel
            variant="vanity"
            icon="eye"
            optionswidth="auto"
            :value="statusDropdown"
            :placeholder="$t('adminHub.defaultView')"
            data-test="page-view-dropdown"
            @optionselected="loadSelectedView($event)"
          >
            <mc-option v-for="(type, key) in viewList" :key="key" :value="key" :label="type[0]"> </mc-option>
          </mc-select>
        </div>

        <div v-if="isPractitioner" class="page-view-dropdown">
          <mc-select
            name="select"
            fit="small"
            hiddenlabel
            variant="vanity"
            icon="eye"
            optionswidth="auto"
            :value="statusDropdown"
            :placeholder="$t('adminHub.defaultView')"
            data-test="page-view-dropdown"
            @optionselected="loadSelectedView($event)"
          >
            <mc-option v-for="(type, key) in practitionerViewList" :key="key" :value="key" :label="type[0]">
            </mc-option>
          </mc-select>
        </div>
      </div>
      <div class="flex items-center gap-4 lg:flex-1 lg:justify-end">
        <mc-button
          v-if="copilotFeatureFlag"
          class="header-ask-legal"
          appearance="primary"
          variant="filled"
          fit="small"
          icon="scale-balance"
          :label="$t('dashboard.copilotChat.launcherLabel')"
          data-test="header-ask-legal"
          @click="openCopilotChat"
        />
        <div class="flex lg:hidden">
          <button
            type="button"
            class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 user-profile"
            @click="openProfileMenu"
          >
            <Bars3Icon class="h-6 w-6" aria-hidden="true" />
          </button>
          <ProfileMenu
            v-if="smallMenuOpen"
            :isMenuOpen="smallMenuOpen"
            :userAPMId="userAPMId"
            :userName="userName"
            :userRole="userRole"
            @close="closeProfileMenu"
          />
        </div>
        <div class="hidden lg:flex">
          <ProfileMenu
            :isMenuOpen="smallMenuOpen"
            :userAPMId="userAPMId"
            :userName="userName"
            :userRole="userRole"
            @close="closeProfileMenu"
          />
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup lang="ts">
import "@maersk-global/mds-components-core/mc-avatar";
import "@maersk-global/mds-components-core/mc-button";
import "@maersk-global/mds-components-core/mc-select";
import "@maersk-global/mds-components-core/mc-option";
import { ref, watch, computed, onMounted } from "vue";
import ProfileMenu from "@/layout/ProfileMenu.vue";
import { Bars3Icon } from "@heroicons/vue/24/outline";
import { useRouter, useRoute } from "vue-router";
import { useGlobals } from "@/utils/globals";
import { getEnvConfig } from "@/config/envConfig";
import DOMPurify from "dompurify";
import { viewList, practitionerViewList } from "@/config/constants";
import { AzureRole, getUIRole } from "@/modules/userProfile/types/UserProfile";
import { useUserProfileStore } from "@/modules/userProfile/store/userProfileStore";
import { requestAssignmentStore } from "@/modules/practitionerWorkbench/store/requestAssignmentStore";
import MaintenanceBanner from "@/layout/MaintenanceBanner.vue";

const router = useRouter();
const route = useRoute(); // Access the current route
const { $t } = useGlobals();
const userProfileStore = useUserProfileStore();
const maerskLogo = ref("/assets/images/Logo.svg");
const lfdLogo = ref("/assets/images/scale-balance.svg");
const userAPMId = ref("");
const userRole = ref("");
const userName = ref("");
const smallMenuOpen = ref(false);
const maerskHomeURL = ref(getEnvConfig().maerskHome);
const redirectUri = ref(getEnvConfig().authRedirectUri);
const maintenanceFlag = ref(String(getEnvConfig().maintenanceActive).trim().toLowerCase() === "true");
const environmentBanner = ref(String(getEnvConfig().environmentBanner).trim().toLowerCase() === "true");
const copilotFeatureFlag = String(getEnvConfig().copilotStudioFeatureFlag).trim().toLowerCase() === "true";

if (userProfileStore.isLoggedIn) {
  userRole.value = getUIRole(userProfileStore.displayUserRole as AzureRole);
  userName.value = userProfileStore.userProfile.fullName;
  userAPMId.value = userProfileStore.userProfile.userId;
}

function openProfileMenu() {
  smallMenuOpen.value = true;
}

function closeProfileMenu() {
  smallMenuOpen.value = false;
}

function openCopilotChat() {
  router.replace({ query: { ...route.query, askLegal: "open" } });
}

async function loadSelectedView(event: any) {
  const loadPage = viewList[event.target.value] ? viewList[event.target.value][1] : "AdminHubView";
  requestAssignmentStore.selectedDropdownRole = event.target.value;
  const adminRoute = router.resolve({
    name: loadPage
  });
  await router.push(adminRoute.href);
}

const statusDropdown = computed(() => {
  return requestAssignmentStore?.selectedDropdownRole;
});

watch(
  () => route.path, // React to changes in the current route path
  (newPath) => {
    const userRole = userProfileStore?.displayUserRole?.split(", ").map((role) => role.trim());
    if (userRole?.includes("ADMIN") || userRole?.includes("LEGAL_PRACTITIONER")) {
      if (newPath === "/practitioner") {
        requestAssignmentStore.selectedDropdownRole = "PRACTITIONER";
      } else if (newPath === "/admin-hub") {
        requestAssignmentStore.selectedDropdownRole = "ADMIN";
      } else if (newPath === "/") {
        requestAssignmentStore.selectedDropdownRole = "EMPLOYEE";
      }
    }
  }
);

const isAdmin = computed(() => {
  return userRole.value === "Admin";
});

const isPractitioner = computed(() => {
  return userRole.value == "Practitioner" || userRole.value == "Concierge";
});

onMounted(async () => {
  if (!userProfileStore.isLoggedIn) {
    const userProfile = await userProfileStore.fetchUserProfile();
    userRole.value = getUIRole(userProfileStore.displayUserRole as AzureRole);
    userName.value = userProfile.fullName;
    userAPMId.value = userProfile.userId;
  }
});
</script>

<style scoped>
@import "@/scss/layout/_header.scss";
</style>
